// src/components/AssignedModules.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useQuery, gql } from "@apollo/client";
import { useLocation } from "react-router-dom";
import Pagination from "../../../../components/Pagination/index.jsx";

// ----------------------------------------------------
// ✅ Queries
// ----------------------------------------------------
const GET_MODULES_BY_CLUSTER = gql`
  query GetModulesByCluster($campusName: String!, $clusterId: String!) {
    getModulesByCluster(campusName: $campusName, clusterId: $clusterId) {
      moduleID
      enabled_dates
    }
  }
`;

const GET_ALL_MODULES = gql`
  query GetAllModules {
    getAllModules {
      _id
      name
      short_description
      daywise_plan {
        day_id
        questions
      }
    }
  }
`;

const GET_MODULE_SUBMISSIONS = gql`
  query GetModuleSubmissions(
    $studentId: String!
    $moduleId: String!
    $campusName: String!
  ) {
    getModuleSubmissions(
      studentId: $studentId
      moduleId: $moduleId
      campusName: $campusName
    ) {
      is_completed
      day_id
      question_id
    }
  }
`;

const formatDate = (iso) => {
  if (!iso) return "N/A";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(
    d.getMonth() + 1
  ).padStart(2, "0")}.${d.getFullYear()}`;
};

const chunkRows = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
};

const ModuleForStudent = () => {
  const location = useLocation();
  const { campusName, clusterId, studentId } = location.state || {};

  const [assignedModules, setAssignedModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const CARDS_PER_PAGE = 6;

  const { data: clusterData } = useQuery(GET_MODULES_BY_CLUSTER, {
    variables: { campusName, clusterId },
  });

  const { data: allModulesData } = useQuery(GET_ALL_MODULES);

  // ✅ Merge modules
  useEffect(() => {
    if (clusterData && allModulesData) {
      const merged = clusterData.getModulesByCluster
        .map(({ moduleID, enabled_dates }) => {
          const mod = allModulesData.getAllModules.find((m) => m._id === moduleID);
          return mod ? { ...mod, enabled_dates } : null;
        })
        .filter(Boolean);

      setAssignedModules(merged);
    }
  }, [clusterData, allModulesData]);

  // ✅ Search Filtering
  const filteredModules = useMemo(() => {
    return assignedModules.filter((m) =>
      m.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [assignedModules, searchTerm]);

  // ✅ Pagination Logic
  const totalPages = Math.ceil(filteredModules.length / CARDS_PER_PAGE);

  const paginatedModules = filteredModules.slice(
    (page - 1) * CARDS_PER_PAGE,
    page * CARDS_PER_PAGE
  );

  const userData = JSON.parse(localStorage.getItem("user"));
  const userDisplayName = userData?.displayName || "Admin";

  return (
    <div className="max-w-7xl mx-auto p-6">

      {/* ✅ Header + Search in Same Line */}
      <div className="flex justify-between items-center mb-10">
        <p className="text-3xl text-gray-900 font-bold">
          Showing modules for{" "}
          <span className="font-semibold text-[#0F537E]">{userDisplayName}</span>
        </p>

        <input
          type="text"
          placeholder="Search modules..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="w-72 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
      </div>

      {/* ✅ Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {paginatedModules.map((module) => (
          <ModuleCard
            key={module._id}
            module={module}
            campusName={campusName}
            studentId={studentId}
          />
        ))}
      </div>

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
};

const ModuleCard = ({ module, studentId, campusName }) => {
  const { data } = useQuery(GET_MODULE_SUBMISSIONS, {
    variables: { studentId, moduleId: module._id, campusName },
  });

  const submissions = data?.getModuleSubmissions || [];

  const getCompletedCount = (day) =>
    submissions.filter(
      (sub) =>
        sub.day_id === day.day_id &&
        sub.is_completed &&
        day.questions.includes(sub.question_id)
    ).length;

  const rows = chunkRows(module.daywise_plan, 5);

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all p-6">
      <h3 className="text-2xl font-bold text-gray-900 mb-2">{module.name}</h3>

      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="grid grid-cols-5 gap-4 mb-5">
          {row.map((day, index) => {
            const total = day.questions.length;
            const completed = getCompletedCount(day);
            const done = completed === total;
            const date = module.enabled_dates[rowIndex * 5 + index];

            return (
              <div
                key={day.day_id}
                className="flex flex-col items-center justify-center bg-white border border-gray-200 rounded-lg shadow-sm w-24 h-20 hover:shadow-md transition-all"
              >
                <p className="text-gray-800 font-medium text-xs">
                  {formatDate(date)}
                </p>

                {done ? (
                  <span className="text-green-600 text-xl font-bold mt-1">✔</span>
                ) : (
                  <span className="mt-1 text-[11px] font-semibold text-blue-700 bg-blue-100 border border-blue-200 px-2 py-[2px] rounded">
                    {completed}/{total}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default ModuleForStudent;
